import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Rocket, Mail, CheckCircle, Sparkles, Zap, Globe, Crown, Link as LinkIcon, DollarSign, User, ExternalLink, Shield, Download, MessageCircle, Gift, Eye, EyeOff, Store, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import Logo from '@/components/Logo';
import ReferralWidget from '@/components/ReferralWidget';
import PublicFeaturedSection from '@/components/PublicFeaturedSection';
import WeeklySpotlightBanner from '@/components/WeeklySpotlightBanner';
import AllFeaturedPartnersStrip from '@/components/AllFeaturedPartnersStrip';
import EcosystemHowItWorks from '@/components/EcosystemHowItWorks';
import PartnerOfTheMonth from '@/components/PartnerOfTheMonth';
import SEOHead, { generateOrganizationSchema, generateWebsiteSchema, generateLaunchEventSchema, generateMarketplaceSchema, generateFAQSchema } from '@/components/SEOHead';
import HomeFAQ, { HOMEPAGE_FAQS } from '@/components/HomeFAQ';
import PressBadge from '@/components/PressBadge';
import NewsletterSignup from '@/components/NewsletterSignup';
import { safeGetJSON } from '@/utils/safeStorage';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// TimeBlock component moved outside to avoid re-creating on every render
const TimeBlock = ({ value, label }) => (
  <div className="flex flex-col items-center">
    <div className="relative">
      <div className="w-20 h-20 sm:w-24 sm:h-24 md:w-28 md:h-28 rounded-2xl bg-gradient-to-br from-[#1a1a2e] to-[#0f0f1a] border border-white/10 flex items-center justify-center shadow-xl">
        <span className="text-3xl sm:text-4xl md:text-5xl font-bold bg-gradient-to-r from-[#00F0FF] to-white bg-clip-text text-transparent">
          {String(value).padStart(2, '0')}
        </span>
      </div>
      <div className="absolute -inset-0.5 bg-gradient-to-r from-[#00F0FF]/20 to-white/20 rounded-2xl blur-lg -z-10"></div>
    </div>
    <span className="mt-3 text-xs sm:text-sm text-neutral-400 uppercase tracking-widest">{label}</span>
  </div>
);

function ComingSoon() {
  const navigate = useNavigate();
  // No fixed launch date — site goes live only when admin manually switches mode
  const [launchDate, setLaunchDate] = useState(null);
  
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loggingIn, setLoggingIn] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  // 2FA challenge state — production admin has 2FA enabled
  const [twoFARequired, setTwoFARequired] = useState(false);
  const [twoFATempToken, setTwoFATempToken] = useState('');
  const [twoFACode, setTwoFACode] = useState('');
  // Forgot Password sub-flow inside the login modal — creators (and admin)
  // sometimes hit this screen without remembering their password. Without a
  // self-serve reset path, support tickets pile up. The "Forgot password?"
  // link toggles `showForgotPassword` which swaps the form to an email-only
  // recovery request that calls /auth/forgot-password.
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSubmitting, setForgotSubmitting] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  
  // Fetch launch date from backend (admin-controlled)
  useEffect(() => {
    const fetchLaunchDate = async () => {
      try {
        const baseUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
        const response = await fetch(`${baseUrl}/api/launch-date?_t=${Date.now()}`, {
          method: 'GET',
          headers: {
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache'
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          if (data.launch_date) {
            setLaunchDate(new Date(data.launch_date));
          }
        }
      } catch (error) {
        console.error('Error fetching launch date:', error);
      }
    };
    fetchLaunchDate();
  }, []);
  
  // Check if user is logged in
  const user = safeGetJSON('user', null);
  const isAdmin = user?.role === 'admin' || user?.role === 'creator' || user?.email?.toLowerCase() === 'ailedgerllc@gmail.com';
  
  // VIP Creator State
  const [showVIPForm, setShowVIPForm] = useState(false);
  const [vipData, setVipData] = useState({
    name: '',
    email: '',
    verification_link: '',
    description: '',
    ownership_declared: false
  });
  const [vipSubmitting, setVipSubmitting] = useState(false);
  const [vipSubmitted, setVipSubmitted] = useState(false);
  const [vipReferralCode, setVipReferralCode] = useState(null);

  // Live Founding Creator counter — counts pending + approved across both
  // application collections, capped at 100. Real number, framed as scarcity.
  const [seatsRemaining, setSeatsRemaining] = useState(null);
  useEffect(() => {
    const baseUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
    fetch(`${baseUrl}/api/public/founding-seats`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d && typeof d.remaining === 'number') setSeatsRemaining(d.remaining); })
      .catch(() => {});
  }, []);

  // Audience toggle — Phase 2 audit fix #1. The hero was pitching creators
  // AND buyers in the same headline, leaving 50/50 of visitors confused
  // about whether the site was "for them". Default to "creator" because
  // it's the highest-LTV audience pre-launch; visitor switches in one click.
  // Persist to localStorage so repeat visitors don't reset.
  const [audience, setAudience] = useState(() => {
    try {
      const stored = localStorage.getItem('preferred_audience');
      if (stored === 'buyer' || stored === 'creator') return stored;
    } catch {
      /* localStorage blocked — fall through to default */
    }
    return 'creator';
  });
  useEffect(() => {
    try { localStorage.setItem('preferred_audience', audience); } catch { /* ignore */ }
  }, [audience]);

  // (Removed previewProducts mini-grid — duplicated PublicFeaturedSection /
  // AllFeaturedPartnersStrip rendered further down on the same page.)

  // FAQ source of truth:
  //   - 5 core Qs live in `<HomeFAQ />` (HOMEPAGE_FAQS) and are reused on
  //     both Home.js and this pre-launch ComingSoon.js — single source = no
  //     drift between visible UI and FAQPage JSON-LD.
  //   - The 2 pre-launch-specific Qs ("Who can become a Founding Creator?",
  //     "When does it launch?") are stitched in here because they don't
  //     apply once the marketplace is live. Inserted at positions 2 and 3
  //     so the reading order is: what → cost → eligibility → launch → payouts → vetting → press.
  const FAQS = [
    HOMEPAGE_FAQS[0],
    HOMEPAGE_FAQS[1],
    {
      question: 'Who can become a Founding Creator?',
      answer:
        'The Founding Creator program is open to any verified creator of an AI product — software, agents, chatbots, automation scripts, prompt libraries, or AI courses. The first 100 approved creators receive lifetime 0% fees for the first 6 months and priority placement in launch marketing.',
    },
    {
      question: 'When does theailedger.ai launch publicly?',
      answer:
        'The public marketplace opens in 2026. The waitlist is live now — join to be notified the moment buying and selling go live, and to lock in your Founding Creator application slot.',
    },
    HOMEPAGE_FAQS[2],
    HOMEPAGE_FAQS[3],
    HOMEPAGE_FAQS[4],
  ];


  const finalizeLogin = (data) => {
    localStorage.setItem('token', data.session_token);
    localStorage.setItem('user', JSON.stringify(data.user));
    toast.success('Logged in successfully!');
    setShowLoginModal(false);
    setTwoFARequired(false);
    setTwoFATempToken('');
    setTwoFACode('');
    navigate('/', { replace: true });
    setTimeout(() => {
      try {
        window.dispatchEvent(new Event('auth-changed'));
      } catch (err) {
        console.debug('auth-changed dispatch failed (non-fatal):', err?.message || err);
      }
    }, 50);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoggingIn(true);
    try {
      const response = await axios.post('/auth/login', {
        email: loginEmail,
        password: loginPassword
      });
      // Branch on 2FA challenge — production admin accounts have 2FA enabled.
      if (response.data.requires_2fa) {
        setTwoFATempToken(response.data.temp_token);
        setTwoFARequired(true);
        setLoggingIn(false);
        return;
      }
      finalizeLogin(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoggingIn(false);
    }
  };

  const handle2FAVerify = async (e) => {
    e.preventDefault();
    if (twoFACode.length !== 6) {
      toast.error('Please enter your 6-digit authenticator code.');
      return;
    }
    setLoggingIn(true);
    try {
      const response = await axios.post('/auth/login/verify-2fa', {
        temp_token: twoFATempToken,
        code: twoFACode,
      });
      finalizeLogin(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid 2FA code');
    } finally {
      setLoggingIn(false);
    }
  };

  // Self-serve password reset from the Coming Soon login modal. Posts to
  // the same /auth/forgot-password endpoint used by the main AuthModal, so
  // creators on the pre-launch landing page have the exact same recovery
  // path as users on the live site.
  const handleForgotPassword = async (e) => {
    e.preventDefault();
    const trimmed = (forgotEmail || '').trim().toLowerCase();
    if (!trimmed) {
      toast.error('Please enter your email');
      return;
    }
    setForgotSubmitting(true);
    try {
      await axios.post('/auth/forgot-password', { email: trimmed });
      setForgotSent(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not send reset email. Please try again.');
    } finally {
      setForgotSubmitting(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.reload();
  };

  useEffect(() => {
    if (!launchDate) return; // No launch date set — skip countdown
    
    const calculateTimeLeft = () => {
      const now = new Date();
      const difference = launchDate - now;

      if (difference > 0) {
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((difference / 1000 / 60) % 60),
          seconds: Math.floor((difference / 1000) % 60)
        });
      } else {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
      }
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);

    return () => clearInterval(timer);
  }, [launchDate]);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    
    if (!email || !email.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);

    try {
      // Propagate the referral code from the URL (if any) so the referrer
      // gets credit for this signup. Matches the ?ref=XXXXX pattern shared
      // by other waitlist signups.
      const refFromUrl = new URLSearchParams(window.location.search).get('ref');
      const payload = { email };
      if (refFromUrl) payload.ref = refFromUrl;
      const resp = await axios.post(`${API_URL}/api/launch-notify`, payload);
      setIsSubscribed(true);
      if (resp.data?.referral_code) {
        localStorage.setItem('waitlist_referral_code', resp.data.referral_code);
      }
      toast.success("You're on the list! We'll notify you when we launch.");
      setEmail('');
    } catch (error) {
      if (error.response?.status === 409) {
        toast.info("You're already on our notification list!");
        setIsSubscribed(true);
      } else {
        toast.error('Something went wrong. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVIPSubmit = async (e) => {
    e.preventDefault();
    
    if (!vipData.email || !vipData.email.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }
    if (!vipData.name.trim()) {
      toast.error('Please enter your name');
      return;
    }
    if (!vipData.verification_link.trim()) {
      toast.error('Please enter a verification link');
      return;
    }
    if (!vipData.ownership_declared) {
      toast.error('Please confirm the product ownership declaration');
      return;
    }
    
    // Basic URL validation
    try {
      new URL(vipData.verification_link);
    } catch {
      toast.error('Please enter a valid URL (include https://)');
      return;
    }

    setVipSubmitting(true);
    
    try {
      const response = await axios.post(`${API_URL}/api/vip-creator-request`, vipData);
      setVipSubmitted(true);
      // Store referral code from VIP application
      if (response.data.referral_code) {
        setVipReferralCode(response.data.referral_code);
        localStorage.setItem('referral_code', response.data.referral_code);
        localStorage.setItem('referral_email', vipData.email);
      }
      toast.success('Application submitted! Check your email for your referral link.');
    } catch (error) {
      if (error.response?.status === 409) {
        // Already submitted - try to fetch their existing referral code
        try {
          const checkResp = await axios.get(`${API_URL}/api/referral/check-email/${encodeURIComponent(vipData.email)}`);
          if (checkResp.data.has_code) {
            setVipReferralCode(checkResp.data.referral_code);
            localStorage.setItem('referral_code', checkResp.data.referral_code);
          }
        } catch (e) { console.error('Referral check failed:', e); }
        toast.info('You\'ve already submitted an application!');
        setVipSubmitted(true);
      } else {
        toast.error('Something went wrong. Please try again.');
      }
    } finally {
      setVipSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white overflow-hidden relative">
      {/* Static background blurs — sized in viewport units so they NEVER
          exceed screen width on mobile. Chrome on Android scales text up if
          it detects content wider than viewport, even when overflow-hidden
          is clipping the visual; vw/vh sizing prevents that trigger.
          NOTE (2026-05-21): animate-pulse + bright white blob combo was
          flagged by a promoter as a "strobe light" because the second blob
          landed exactly behind the "How it works" cards and pulsed in/out.
          Pulse removed; white blob opacity dropped from /10 to /[0.03] so
          it sits behind the hero without flickering the cards below. */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-[60vw] h-[60vw] md:w-96 md:h-96 bg-[#00F0FF]/[0.06] rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 right-1/4 w-[60vw] h-[60vw] md:w-96 md:h-96 bg-white/[0.03] rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[90vw] h-[90vw] md:w-[600px] md:h-[600px] bg-gradient-to-r from-[#00F0FF]/[0.04] to-white/[0.03] rounded-full blur-3xl"></div>
      </div>

      {/* Grid Pattern Overlay */}
      <div className="hidden md:block absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:50px_50px]"></div>

      <div className="relative z-10 min-h-screen flex flex-col">
        {/* Skip-to-content link — Phase 3 accessibility audit fix.
            Visible only on keyboard focus; lets sighted-keyboard and screen
            reader users jump past the nav directly to the hero content. */}
        <a
          href="#main"
          className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-50 focus:px-4 focus:py-2 focus:bg-[#00F0FF] focus:text-black focus:font-bold focus:rounded-md"
          data-testid="skip-to-content"
        >
          Skip to main content
        </a>
        <SEOHead
          title="theailedger.ai — The AI Tool Marketplace for Creators & Buyers"
          description="theailedger.ai is the launching marketplace for AI tools, software, and digital products. First 100 creators get Founding Creator status — 0% fees for 6 months. Apply now."
          path="/"
          keywords="AI marketplace launch, buy AI tools online, sell AI products, AI software marketplace, AI tools store, artificial intelligence marketplace, AI product marketplace, AI digital products, AI automation tools, best AI tools 2026, AI chatbot marketplace, AI prompt marketplace, AI business tools, AI creator platform, AI entrepreneur tools, where to buy AI tools, AI tools for business, AI SaaS marketplace, machine learning marketplace, AI solution marketplace"
          schema={{
            '@context': 'https://schema.org',
            '@graph': [
              generateWebsiteSchema(),
              generateOrganizationSchema(),
              generateLaunchEventSchema(),
              generateMarketplaceSchema(),
              generateFAQSchema(FAQS),
            ]
          }}
        />
        {/* Launch announcement bar — sits above the nav. Dismissable with
            localStorage memory so returning visitors don't see it twice.
            Auto-hides itself after 2026-06-03 (launch instant) so it never
            stays up post-launch even if the site doesn't redeploy. */}
        <LaunchAnnouncementBar />

        {/* Header */}
        <header className="p-4 md:p-8">
          <div className="flex items-center justify-between max-w-7xl mx-auto gap-2">
            <div className="flex items-center gap-2 flex-shrink-0">
              <Logo />
            </div>
            <div className="flex items-center gap-1 md:gap-3 justify-end flex-nowrap">
              {/* Featured Tools nav — public, discoverable.
                  Icon-only on mobile (~32px tap target) so it always fits
                  next to Sell + Sign In on a 360–390px phone without wrap. */}
              <Button
                onClick={() => navigate('/featured')}
                variant="outline"
                size="sm"
                className="border-[#00F0FF]/30 bg-[#00F0FF]/5 hover:bg-[#00F0FF]/15 text-[#00F0FF] font-medium whitespace-nowrap px-2 md:px-4 text-xs md:text-sm"
                data-testid="header-featured-tools-nav"
                aria-label="Featured AI Tools"
                title="Featured AI Tools"
              >
                <Sparkles className="w-4 h-4 md:mr-2" />
                <span className="hidden md:inline">Featured Tools</span>
              </Button>
              {/* Sell CTA — always visible. Renamed from "Sell your AI tool" to
                  "Apply as Founding Creator" so it reads as an active gate, not
                  a generic action. Audit fix: "Founding" wording reinforces the
                  scarcity / status positioning of the program. */}
              <Button
                onClick={() => navigate('/submit')}
                size="sm"
                className="bg-[#00F0FF] hover:bg-[#00F0FF]/90 text-black font-semibold whitespace-nowrap px-3 md:px-4 text-xs md:text-sm"
                data-testid="header-submit-cta"
              >
                <span className="hidden md:inline">Apply as Founding Creator</span>
                <span className="md:hidden">Apply →</span>
              </Button>
              {user ? (
                <>
                  {isAdmin && (
                    <Button
                      onClick={() => navigate('/admin')}
                      size="sm"
                      className="bg-red-500/20 hover:bg-red-500/30 text-red-400 border border-red-500/30 px-2 md:px-4 text-xs md:text-sm"
                    >
                      <Shield className="w-3 h-3 md:w-4 md:h-4 md:mr-2" />
                      <span className="hidden md:inline">Admin</span>
                    </Button>
                  )}
                  <Button
                    onClick={() => navigate('/chat')}
                    variant="outline"
                    size="sm"
                    className="border-[#00FF94]/30 text-[#00FF94] hover:bg-[#00FF94]/10 px-2 md:px-4 text-xs md:text-sm"
                  >
                    <MessageCircle className="w-3 h-3 md:w-4 md:h-4 md:mr-2" />
                    <span className="hidden md:inline">Chat</span>
                  </Button>
                  <Button
                    onClick={() => navigate('/dashboard')}
                    variant="outline"
                    size="sm"
                    className="border-white/30 text-white hover:bg-[#00F0FF]/10 hover:text-[#00F0FF] hover:border-[#00F0FF]/50 px-2 md:px-4 text-xs md:text-sm"
                  >
                    <span className="hidden md:inline">Dashboard</span>
                    <span className="md:hidden">Dash</span>
                  </Button>
                  <Button
                    onClick={handleLogout}
                    variant="ghost"
                    size="sm"
                    className="text-neutral-400 hover:text-white px-2 md:px-4 text-xs md:text-sm"
                  >
                    <span className="hidden md:inline">Logout</span>
                    <span className="md:hidden">Exit</span>
                  </Button>
                </>
              ) : (
                <Button
                  onClick={() => setShowLoginModal(true)}
                  className="bg-[#00F0FF] hover:bg-[#00C2CC] text-black font-semibold text-sm"
                >
                  Sign In
                </Button>
              )}
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main id="main" className="flex-1 flex items-center justify-center px-4 sm:px-6 py-6 sm:py-10 md:py-12 min-w-0 w-full">
          <div className="w-full max-w-4xl mx-auto text-center min-w-0">
            {/* Badge — replaced static "Launching Soon" with a live spots-remaining
                indicator that links to the apply form. Audit fix: gives the badge
                actionable intent + makes the scarcity feel real (number is computed
                from pending+approved Founding Creator applications, not fabricated).
                Spacing: tightened mb-8 → mb-4 on 2026-05-21 so the headline + 0%
                fee subtitle + CTA all land inside the upper third of a 9:16
                reel capture without scrolling. */}
            <button
              onClick={() => navigate('/submit')}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#00F0FF]/10 border border-[#00F0FF]/30 mb-4 hover:bg-[#00F0FF]/15 hover:border-[#00F0FF]/50 transition-colors cursor-pointer"
              data-testid="hero-spots-badge"
              aria-label="View Founding Creator program — spots remaining"
            >
              <Crown className="w-4 h-4 text-[#00F0FF]" />
              <span className="text-sm text-[#00F0FF] font-medium">
                {/* Public count hidden per founder request — applicants
                    still receive their assigned ambassador number via
                    confirmation email. Surfacing "57 spots remaining"
                    publicly was felt to under-sell the scarcity story
                    once the number drifts close to/past zero. The badge
                    stays as a generic "limited spots" pill so the
                    program framing still lands without exposing the
                    raw counter. */}
                {seatsRemaining === 0
                  ? 'Founding Creator program closed'
                  : 'Founding Creator program — limited spots'}
              </span>
            </button>

            {/* Audience toggle — Phase 2 audit fix #1. Visitors self-select
                in one click; headline, subtitle, and primary CTA all adapt.
                Default is creators (highest-LTV pre-launch audience). */}
            <div
              className="inline-flex items-center gap-1 p-1 rounded-full bg-white/[0.04] border border-white/10 mb-5"
              role="tablist"
              aria-label="Choose audience"
              data-testid="audience-toggle"
            >
              <button
                type="button"
                role="tab"
                aria-selected={audience === 'creator'}
                onClick={() => setAudience('creator')}
                className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${
                  audience === 'creator'
                    ? 'bg-[#00F0FF] text-black'
                    : 'text-neutral-300 hover:text-white'
                }`}
                data-testid="audience-toggle-creator"
              >
                I&apos;m a creator
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={audience === 'buyer'}
                onClick={() => setAudience('buyer')}
                className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${
                  audience === 'buyer'
                    ? 'bg-[#00F0FF] text-black'
                    : 'text-neutral-300 hover:text-white'
                }`}
                data-testid="audience-toggle-buyer"
              >
                I&apos;m a buyer
              </button>
            </div>

            {/* Main Heading — audience-aware. The previous dual-headline pitched
                creators AND buyers in one line and 50/50 of audit visitors said
                it confused them. Now each audience gets a single, focused promise. */}
            <h1
              className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-4 leading-tight text-white"
              data-testid="hero-headline"
            >
              {audience === 'creator' ? (
                <>
                  List your AI tool.{' '}
                  <br />
                  <span className="text-white">0% fees for 6 months.</span>
                </>
              ) : (
                <>
                  Discover AI tools{' '}
                  <br />
                  <span className="text-white">built to generate real income.</span>
                </>
              )}
            </h1>

            {/* Subtitle — single, audience-specific value prop. */}
            <p
              className="text-base sm:text-lg text-neutral-300 leading-relaxed max-w-2xl mx-auto mb-6"
              data-testid="hero-subtitle"
            >
              {audience === 'creator' ? (
                <>
                  Join the first 100 Founding Creators. <span className="text-[#00F0FF] font-semibold">0% marketplace fees for 6 months</span>, then 5% per sale — no monthly fee, no listing fee. Worldwide payouts via Stripe Connect.
                </>
              ) : (
                <>
                  Curated AI agents, chatbots, automation, and software from creators worldwide. <span className="text-[#00F0FF] font-semibold">Vetted for quality</span>, organized across 52 categories, ranked by what actually earns.
                </>
              )}
            </p>

            {/* As Seen On Badge — shared <PressBadge /> component so markup
                stays in sync with Home.js. Audit fix #6: unverifiable media
                logos erode trust; clickable proof builds it. */}
            <PressBadge
              onClick={() => { navigate('/press') }}
              className="flex justify-center mb-5"
              testId="as-seen-on-badge"
            />

            {/* Social Proof Stats — audience-aware middle stat. Seller side
                shows the 0% / 6-month founding-creator fee; buyer side swaps
                to "$0 to browse" since marketplace fees are irrelevant to a
                buyer's decision to use the site. */}
            <div className="flex flex-wrap items-center justify-center gap-6 md:gap-10 mb-7" data-testid="coming-soon-stats">
              <div className="text-center">
                <p className="text-2xl md:text-3xl font-black text-white">52</p>
                <p className="text-xs text-neutral-500 mt-0.5">AI categories</p>
              </div>
              <div className="w-px h-8 bg-white/10 hidden sm:block" />
              <div className="text-center">
                {audience === 'creator' ? (
                  <>
                    <p className="text-2xl md:text-3xl font-black text-white">0%</p>
                    <p className="text-xs text-neutral-500 mt-0.5">Fee for 6 months</p>
                    <p className="text-[10px] text-neutral-600 mt-0.5">Then 5% per sale · No monthly fee</p>
                  </>
                ) : (
                  <>
                    <p className="text-2xl md:text-3xl font-black text-white">$0</p>
                    <p className="text-xs text-neutral-500 mt-0.5">To browse &amp; compare</p>
                    <p className="text-[10px] text-neutral-600 mt-0.5">No subscription · No signup wall</p>
                  </>
                )}
              </div>
              <div className="w-px h-8 bg-white/10 hidden sm:block" />
              <div className="text-center">
                <button
                  onClick={() => { navigate('/press') }}
                  className="text-2xl md:text-3xl font-black text-white hover:text-[#00F0FF] transition-colors cursor-pointer"
                  data-testid="footer-press-stat"
                  aria-label="View press coverage"
                >
                  4
                </button>
                <p className="text-xs text-neutral-500 mt-0.5">Network broadcasts</p>
                <p className="text-[10px] text-neutral-600 mt-0.5">CBS · NBC · FOX · ABC</p>
              </div>
            </div>

            {/* Founding Creator fee disclaimer — added per launch-day
                founder request to make the offer terms explicit on the
                landing page (first-100 cap + flat-5% post-perk fee). The
                same line is mirrored on the post-launch Home.js hero. */}
            {audience === 'creator' && (
              <p
                className="text-center text-xs text-neutral-500 mt-4 mb-10 max-w-xl mx-auto"
                data-testid="hero-fee-disclaimer"
              >
                First 100 creators get 0% for 6 months, then flat 5% — no subscription
              </p>
            )}

            {/* Email Signup — Phase 2 audit fix #4C: CTA copy is now
                audience-aware. The previous "Get Early Access" was vague
                enough that visitors didn't know what clicking did; now creators
                see "Apply as Founding Creator" (sends them to the application)
                and buyers see "Notify me at launch" (joins waitlist). Form is
                also a11y-improved with a real <label>, aria-describedby, and
                inputMode="email" for mobile keyboards. */}
            <div className="max-w-md mx-auto mb-16">
              {!isSubscribed ? (
                <form onSubmit={(e) => {
                  // Creators get routed to the application; buyers stay on
                  // the email waitlist. Stops the "I just typed my email,
                  // why am I at the waitlist?" mismatch for the creator side.
                  if (audience === 'creator') {
                    e.preventDefault();
                    if (email) localStorage.setItem('prefill_email', email);
                    navigate('/submit');
                    return;
                  }
                  handleSubscribe(e);
                }} className="relative" data-testid="hero-signup-form">
                  <label htmlFor="hero-email-input" className="sr-only">
                    {audience === 'creator' ? 'Your email — Founding Creator application' : 'Your email — launch notification'}
                  </label>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <div className="relative flex-1">
                      <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-neutral-500" aria-hidden="true" />
                      <Input
                        id="hero-email-input"
                        type="email"
                        inputMode="email"
                        autoComplete="email"
                        placeholder={audience === 'creator' ? 'Your email (we’ll pre-fill the application)' : 'Enter your email'}
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        aria-describedby="hero-email-helper"
                        required
                        className="w-full pl-12 pr-4 py-6 bg-white/5 border-white/10 rounded-xl text-white placeholder:text-neutral-500 focus:border-[#00F0FF]/50 focus:ring-[#00F0FF]/20"
                      />
                    </div>
                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="px-8 py-6 bg-[#00F0FF] hover:bg-[#00C2CC] disabled:opacity-60 text-black font-bold rounded-xl transition-all duration-200 shadow-lg shadow-[#00F0FF]/20 whitespace-nowrap"
                      data-testid="hero-email-submit"
                    >
                      {isSubmitting
                        ? 'Joining…'
                        : audience === 'creator'
                          ? 'Apply as Founding Creator →'
                          : 'Notify me at launch →'}
                    </Button>
                  </div>
                  <p id="hero-email-helper" className="text-xs text-neutral-500 mt-3">
                    {audience === 'creator'
                      ? 'Takes about 3 minutes. We review every applicant within 24 hours.'
                      : 'Be the first to know when we launch. No spam, ever.'}
                  </p>
                </form>
              ) : (
                <div className="flex items-center justify-center gap-3 p-6 rounded-xl bg-green-500/10 border border-green-500/30" role="status">
                  <CheckCircle className="w-6 h-6 text-green-500" aria-hidden="true" />
                  <span className="text-green-400 font-medium">You&apos;re on the list! We&apos;ll email you when we launch.</span>
                </div>
              )}

              {/* Creators: free listing optimizer (top-of-funnel lead magnet).
                  Surface only when the creator audience toggle is active so
                  the homepage stays focused for buyers. */}
              {audience === 'creator' && !isSubscribed ? (
                <div className="mt-5 text-center">
                  <button
                    onClick={() => navigate('/optimizer')}
                    data-testid="hero-optimizer-cta"
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-white/[0.03] hover:bg-white/[0.06] border border-[#00F0FF]/40 hover:border-[#00F0FF] text-[#00F0FF] text-sm font-semibold transition"
                  >
                    Not ready? Get a free AI tool audit + earnings estimate →
                  </button>
                  <p className="text-xs text-neutral-500 mt-2">
                    Paste your URL · See your score, conservative monthly earnings range, and top fixes · No signup
                  </p>
                </div>
              ) : null}
            </div>

            {/* 30-second teaser video — sits in the hero column between
                the Optimizer CTA and the Referral widget. Click-to-play
                (NOT autoplay) so mobile doesn't burn data unprompted and
                the page weight stays light. `preload="metadata"` pulls
                just the duration/dimensions so the poster + Play button
                render instantly. Single source of truth (mp4) — Safari,
                Chrome, Firefox, mobile all support H.264. */}
            <div className="max-w-2xl mx-auto mb-8" data-testid="hero-teaser-video-wrap">
              <div className="rounded-2xl overflow-hidden border border-[#00F0FF]/20 shadow-[0_0_60px_rgba(0,240,255,0.10)] bg-black">
                <video
                  src="/videos/teaser.mp4"
                  className="w-full h-auto block"
                  controls
                  playsInline
                  preload="metadata"
                  poster=""
                  data-testid="hero-teaser-video"
                  aria-label="30-second teaser: what theailedger.ai is building"
                />
              </div>
              <p className="text-center text-xs text-neutral-500 mt-2">
                A 30-second look at what we're building
              </p>
            </div>

            {/* Referral Program Widget */}
            <div className="max-w-md mx-auto mb-8">
              {user ? (
                // Logged-in user - show link to full dashboard
                <div className="bg-gradient-to-r from-white/20 to-[#00F0FF]/20 border border-white/10 rounded-2xl p-6 text-center">
                  <Gift className="w-8 h-8 text-[#00F0FF] mx-auto mb-3" />
                  <h3 className="font-bold text-lg mb-2">Your Referral Dashboard</h3>
                  <p className="text-neutral-300 text-sm mb-4">
                    Share your unique link and earn rewards for every friend who joins!
                  </p>
                  <Button
                    onClick={() => navigate('/referrals')}
                    className="bg-gradient-to-r from-[#00F0FF] to-white hover:opacity-90 text-white font-bold"
                  >
                    <Gift className="w-4 h-4 mr-2" />
                    View Referrals & Share Link
                  </Button>
                </div>
              ) : (
                <ReferralWidget />
              )}
            </div>

            {/* (Above-fold product mini-grid removed — duplicate of the
                PublicFeaturedSection / AllFeaturedPartnersStrip already
                rendered further down. Keeping one source of truth for
                "what's already on the marketplace" lets that section move
                up to fill the visual space.) */}

            {/* Features Preview */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-4xl mx-auto">
              <div className="p-6 rounded-2xl bg-white/5 border border-white/10 backdrop-blur-sm">
                <div className="w-12 h-12 rounded-xl bg-[#00F0FF]/10 flex items-center justify-center mb-4 mx-auto">
                  <Sparkles className="w-6 h-6 text-[#00F0FF]" />
                </div>
                <h3 className="font-semibold mb-2">AI Products</h3>
                <p className="text-sm text-neutral-400">Curated collection across 52 categories</p>
              </div>
              
              <div className="p-6 rounded-2xl bg-white/5 border border-white/10 backdrop-blur-sm">
                <div className="w-12 h-12 rounded-xl bg-[#00F0FF]/10 flex items-center justify-center mb-4 mx-auto">
                  <Zap className="w-6 h-6 text-[#00F0FF]" />
                </div>
                <h3 className="font-semibold mb-2">Instant Access</h3>
                <p className="text-sm text-neutral-400">Download immediately after purchase</p>
              </div>
              
              <div className="p-6 rounded-2xl bg-white/5 border border-white/10 backdrop-blur-sm">
                <div className="w-12 h-12 rounded-xl bg-[#00F0FF]/10 flex items-center justify-center mb-4 mx-auto">
                  <Globe className="w-6 h-6 text-[#00F0FF]" />
                </div>
                <h3 className="font-semibold mb-2">Sell Your AI</h3>
                <p className="text-sm text-neutral-400">Become a creator and earn instantly</p>
              </div>

              <div className="p-6 rounded-2xl bg-white/5 border border-white/10 backdrop-blur-sm">
                <div className="w-12 h-12 rounded-xl bg-[#00F0FF]/10 flex items-center justify-center mb-4 mx-auto">
                  <DollarSign className="w-6 h-6 text-[#00F0FF]" />
                </div>
                <h3 className="font-semibold mb-2">Affiliate Program</h3>
                <p className="text-sm text-neutral-400">Earn commissions promoting AI tools</p>
              </div>
            </div>

            {/* Public Featured AI Tools — social proof for pre-launch visitors.
                Auto-populates from /api/affiliate-partners as new partners are
                approved via /admin/affiliate-partners. */}
            <div className="max-w-3xl mx-auto px-4 mb-2">
              <WeeklySpotlightBanner />
            </div>
            <PublicFeaturedSection />
            <AllFeaturedPartnersStrip />

            {/* Top Featured Partner this month — same component as on
                Home so pre/post-launch visitors see identical social
                proof. */}
            <PartnerOfTheMonth />

            {/* Same visual explainer as the post-launch Home page so
                visitors get the identical pitch whether the marketplace
                is gated or open. CTA hidden here because the "Start
                Selling Your AI" card directly below already drives the
                same creator-conversion action AND actually opens the
                application form (this section's CTA only scrolled
                without opening anything — redundant + broken UX). */}
            <EcosystemHowItWorks hideCta />

            {/* Start Selling Your AI */}
            <div className="mt-16 max-w-2xl mx-auto">
              {!showVIPForm ? (
                <div className="p-8 rounded-2xl bg-white/5 border border-white/20 text-center" data-testid="seller-cta-section">
                  <div className="w-16 h-16 rounded-full bg-white flex items-center justify-center mx-auto mb-4">
                    <Store className="w-8 h-8 text-black" />
                  </div>
                  <h3 className="text-2xl font-bold mb-2 text-white">
                    Start Selling Your AI
                  </h3>
                  <p className="text-neutral-400 mb-2">
                    Apply to sell &rarr; Get approved in 24hrs &rarr; Start earning
                  </p>
                  <p className="text-sm text-neutral-500 mb-6">
                    Only a 5% platform fee. Built-in audience, analytics, and affiliate program.
                  </p>
                  <Button
                    onClick={() => setShowVIPForm(true)}
                    className="bg-white hover:bg-neutral-200 text-black font-bold px-8 py-6 rounded-xl"
                    data-testid="start-selling-btn"
                  >
                    <Store className="w-5 h-5 mr-2" />
                    Start Selling Your AI
                  </Button>
                </div>
              ) : vipSubmitted ? (
                <div className="p-8 rounded-2xl bg-gradient-to-br from-green-500/10 to-emerald-500/10 border border-green-500/30 text-center">
                  <div className="w-16 h-16 rounded-full bg-green-500 flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-green-400 mb-2">Application Submitted!</h3>
                  <p className="text-neutral-400 mb-4">
                    We&apos;ll review your application and get back to you within 24 hours with your seller access.
                  </p>
                  {vipReferralCode && (
                    <div className="mt-6 p-4 bg-white/5 border border-white/20 rounded-xl">
                      <p className="text-sm text-white font-bold mb-2 flex items-center justify-center gap-2">
                        <Gift className="w-4 h-4" /> Your Referral Link
                      </p>
                      <div className="flex items-center gap-2 bg-black/50 rounded-lg p-3">
                        <input 
                          type="text" 
                          readOnly 
                          value={`https://theailedger.ai?ref=${vipReferralCode}`}
                          className="flex-1 bg-transparent text-[#00F0FF] text-sm outline-none"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            navigator.clipboard.writeText(`https://theailedger.ai?ref=${vipReferralCode}`);
                            toast.success('Referral link copied!');
                          }}
                          className="px-3 py-1 bg-white hover:bg-neutral-200 text-black rounded-md text-xs font-bold"
                        >
                          Copy
                        </button>
                      </div>
                      <p className="text-xs text-neutral-400 mt-2">
                        Share with other creators — earn rewards for each one who joins.
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="p-8 rounded-2xl bg-white/5 border border-white/20" data-testid="seller-application-form">
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center">
                        <Store className="w-6 h-6 text-black" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-white">Seller Application</h3>
                        <p className="text-sm text-neutral-400">Apply to sell &rarr; Approved in 24hrs</p>
                      </div>
                    </div>
                    <button
                      onClick={() => setShowVIPForm(false)}
                      className="text-neutral-400 hover:text-white"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                  <form onSubmit={handleVIPSubmit} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-neutral-300 mb-2">
                        <User className="w-4 h-4 inline mr-2" />
                        Your Name
                      </label>
                      <Input
                        type="text"
                        placeholder="John Smith"
                        value={vipData.name}
                        onChange={(e) => setVipData({ ...vipData, name: e.target.value })}
                        className="bg-black/30 border-white/10 text-white placeholder:text-neutral-500"
                        required
                        data-testid="seller-name-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-neutral-300 mb-2">
                        <Mail className="w-4 h-4 inline mr-2" />
                        Email Address
                      </label>
                      <Input
                        type="email"
                        placeholder="you@example.com"
                        value={vipData.email}
                        onChange={(e) => setVipData({ ...vipData, email: e.target.value })}
                        className="bg-black/30 border-white/10 text-white placeholder:text-neutral-500"
                        required
                        data-testid="seller-email-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-neutral-300 mb-2">
                        <LinkIcon className="w-4 h-4 inline mr-2" />
                        Product or Portfolio URL
                      </label>
                      <Input
                        type="url"
                        placeholder="https://yourproduct.com or https://github.com/yourprofile"
                        value={vipData.verification_link}
                        onChange={(e) => setVipData({ ...vipData, verification_link: e.target.value })}
                        className="bg-black/30 border-white/10 text-white placeholder:text-neutral-500"
                        required
                        data-testid="seller-url-input"
                      />
                      <p className="text-xs text-neutral-500 mt-1">
                        Link to your AI product, portfolio, or profile so we can verify you
                      </p>
                    </div>
                    <div className="flex items-start gap-3 p-4 bg-black/20 rounded-lg border border-white/5">
                      <input
                        type="checkbox"
                        id="vip-ownership-declaration"
                        checked={vipData.ownership_declared}
                        onChange={(e) => setVipData({ ...vipData, ownership_declared: e.target.checked })}
                        className="mt-1 w-4 h-4 rounded border-white/20 bg-neutral-800 text-white focus:ring-white focus:ring-offset-0 cursor-pointer"
                        required
                      />
                      <label htmlFor="vip-ownership-declaration" className="text-sm text-neutral-400 cursor-pointer">
                        <span className="text-white font-medium">I declare</span> that all products I will list on theailedger.ai are created by me, owned by me, or I have legal rights to sell them.
                      </label>
                    </div>
                    <Button
                      type="submit"
                      disabled={vipSubmitting || !vipData.ownership_declared}
                      className="w-full bg-white hover:bg-neutral-200 text-black font-bold py-6 rounded-xl disabled:opacity-50"
                      data-testid="submit-seller-application-btn"
                    >
                      {vipSubmitting ? (
                        'Submitting...'
                      ) : (
                        <>
                          <ExternalLink className="w-5 h-5 mr-2" />
                          Submit Application
                        </>
                      )}
                    </Button>
                    <p className="text-xs text-neutral-500 text-center">
                      Applications are typically approved within 24 hours
                    </p>
                  </form>
                </div>
              )}
            </div>
          </div>
        </main>

        {/* FAQ — Phase 3 audit fix: AI Citability "Answer Clarity" scored 28/100
            because we had no canonical Q&A blocks. This section is rendered
            visibly for visitors AND mirrored into FAQPage JSON-LD via the
            schema above, so LLMs (ChatGPT, Perplexity, Gemini) and Google
            snippet engines can quote us directly. */}
        {/* FAQ — single source of truth in <HomeFAQ />. Both homepages
            (Home.js + this pre-launch ComingSoon.js) render the same
            component; we pass the pre-launch superset here so the
            "Who can become a Founding Creator?" and launch-timing Qs
            still surface during waitlist. */}
        <HomeFAQ
          faqs={FAQS}
          onFullSetClick={() => { navigate('/faqs') }}
        />

        {/* The AI Ledger Brief — newsletter signup. Coming-soon visitors
            who scrolled past the FAQ are pre-qualified email-list gold:
            they hit the page, scrolled, didn't apply or convert, but are
            still curious. Capture them with the weekly brief instead of
            losing the visit entirely. */}
        <section className="container mx-auto px-4 sm:px-6 mt-10 mb-12 max-w-3xl" data-testid="coming-soon-newsletter-section">
          <NewsletterSignup source="coming_soon_footer" />
        </section>

        {/* Footer */}
        <footer className="p-6 text-center space-y-3">
          <div className="flex flex-wrap justify-center gap-x-4 gap-y-2 text-xs sm:text-sm">
            <button onClick={() => { navigate('/submit') }} className="text-[#00F0FF] hover:text-[#00F0FF]/80 font-semibold transition-colors" data-testid="footer-submit-link">Sell Your AI Tool</button>
            <button onClick={() => { navigate('/press') }} className="text-neutral-400 hover:text-[#00F0FF] transition-colors" data-testid="footer-press-link">Press</button>
            <button onClick={() => { navigate('/resources') }} className="text-neutral-400 hover:text-[#00F0FF] transition-colors">Resources</button>
            <button onClick={() => { navigate('/leaderboard') }} className="text-neutral-400 hover:text-[#00F0FF] transition-colors">Leaderboard</button>
            <button onClick={() => { navigate('/terms') }} className="text-neutral-400 hover:text-[#00F0FF] transition-colors">Terms</button>
            <button onClick={() => { navigate('/privacy') }} className="text-neutral-400 hover:text-[#00F0FF] transition-colors">Privacy</button>
            <button onClick={() => { navigate('/contact') }} className="text-neutral-400 hover:text-[#00F0FF] transition-colors">Contact</button>
          </div>
          <p className="text-xs sm:text-sm text-neutral-500">
            © 2026 theailedger.ai - The Marketplace for AI Products
          </p>
        </footer>
      </div>

      {/* Login Modal */}
      {showLoginModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-neutral-900 border border-white/10 rounded-2xl p-8 max-w-md w-full">
            {twoFARequired ? (
              <>
                <h2 className="text-2xl font-bold mb-2">Two-Factor Authentication</h2>
                <p className="text-neutral-400 text-sm mb-6">
                  Enter the 6-digit code from your authenticator app.
                </p>
                <form onSubmit={handle2FAVerify} className="space-y-4" data-testid="coming-soon-2fa-form">
                  <Input
                    autoFocus
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={6}
                    value={twoFACode}
                    onChange={(e) => setTwoFACode(e.target.value.replace(/\D/g, ''))}
                    placeholder="123456"
                    className="bg-neutral-800 border-white/10 text-center tracking-[0.6em] text-2xl font-mono py-6"
                    required
                    data-testid="coming-soon-2fa-input"
                  />
                  <div className="flex gap-3 pt-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setTwoFARequired(false);
                        setTwoFACode('');
                        setTwoFATempToken('');
                      }}
                      className="flex-1 border-white/20"
                      data-testid="coming-soon-2fa-back"
                    >
                      Back
                    </Button>
                    <Button
                      type="submit"
                      disabled={loggingIn || twoFACode.length !== 6}
                      className="flex-1 bg-[#00F0FF] hover:bg-[#00C2CC] text-black font-bold"
                      data-testid="coming-soon-2fa-verify"
                    >
                      {loggingIn ? 'Verifying...' : 'Verify'}
                    </Button>
                  </div>
                </form>
              </>
            ) : showForgotPassword ? (
              <>
                <h2 className="text-2xl font-bold mb-2">Reset your password</h2>
                <p className="text-neutral-400 text-sm mb-6">
                  Enter your account email and we&apos;ll send you a secure
                  link to choose a new password.
                </p>
                {forgotSent ? (
                  <div className="space-y-4">
                    <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4">
                      <p className="text-emerald-300 text-sm">
                        If an account exists for{' '}
                        <strong className="text-emerald-200">{forgotEmail}</strong>,
                        a reset link is on its way. Check your inbox (and spam
                        folder) — the link expires in 1 hour.
                      </p>
                    </div>
                    <Button
                      type="button"
                      onClick={() => {
                        setShowForgotPassword(false);
                        setForgotSent(false);
                        setForgotEmail('');
                      }}
                      className="w-full bg-[#00F0FF] hover:bg-[#00C2CC] text-black font-bold"
                      data-testid="coming-soon-forgot-back-to-login"
                    >
                      Back to Sign In
                    </Button>
                  </div>
                ) : (
                  <form onSubmit={handleForgotPassword} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Email</label>
                      <Input
                        type="email"
                        value={forgotEmail}
                        onChange={(e) => setForgotEmail(e.target.value)}
                        placeholder="you@example.com"
                        className="bg-neutral-800 border-white/10"
                        required
                        data-testid="coming-soon-forgot-email"
                      />
                    </div>
                    <div className="flex gap-3 pt-4">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          setShowForgotPassword(false);
                          setForgotEmail('');
                        }}
                        className="flex-1 border-white/20"
                        data-testid="coming-soon-forgot-cancel"
                      >
                        Back
                      </Button>
                      <Button
                        type="submit"
                        disabled={forgotSubmitting}
                        className="flex-1 bg-[#00F0FF] hover:bg-[#00C2CC] text-black font-bold"
                        data-testid="coming-soon-forgot-submit"
                      >
                        {forgotSubmitting ? 'Sending...' : 'Send Reset Link'}
                      </Button>
                    </div>
                  </form>
                )}
              </>
            ) : (
              <>
                <h2 className="text-2xl font-bold mb-2">Sign In</h2>
                <p className="text-neutral-400 text-sm mb-6">Sign in to your account</p>
                <form onSubmit={handleLogin} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Email</label>
                    <Input
                      type="email"
                      value={loginEmail}
                      onChange={(e) => setLoginEmail(e.target.value)}
                      placeholder="you@example.com"
                      className="bg-neutral-800 border-white/10"
                      required
                      data-testid="coming-soon-login-email"
                    />
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-medium">Password</label>
                      <button
                        type="button"
                        onClick={() => {
                          setShowForgotPassword(true);
                          setForgotEmail(loginEmail);
                        }}
                        className="text-xs text-[#00F0FF] hover:text-[#00C2CC] transition-colors"
                        data-testid="coming-soon-forgot-password-link"
                      >
                        Forgot password?
                      </button>
                    </div>
                    <div className="relative">
                      <Input
                        type={showPassword ? "text" : "password"}
                        value={loginPassword}
                        onChange={(e) => setLoginPassword(e.target.value)}
                        placeholder="••••••••"
                        className="bg-neutral-800 border-white/10 pr-10"
                        required
                        data-testid="coming-soon-login-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-white transition-colors"
                        tabIndex={-1}
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  <div className="flex gap-3 pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setShowLoginModal(false)}
                      className="flex-1 border-white/20"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      disabled={loggingIn}
                      className="flex-1 bg-[#00F0FF] hover:bg-[#00C2CC] text-black font-bold"
                      data-testid="coming-soon-login-submit"
                    >
                      {loggingIn ? 'Signing in...' : 'Sign In'}
                    </Button>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      )}

      {/* CSS for gradient animation */}
      <style>{`
        @keyframes gradient {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        .animate-gradient {
          animation: gradient 3s ease infinite;
        }
      `}</style>

      {/* Mobile sticky bottom CTA — audit fix #8: primary action was at fold
          depth 3 on mobile, mobile-first visitors had no reason to scroll past
          the hero. Now the email capture is one tap away regardless of scroll
          position. Hidden on md+ where the desktop hero already covers the CTA. */}
      {!isSubscribed && !showLoginModal && (
        <div
          className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-black/85 backdrop-blur-md border-t border-[#00F0FF]/20 px-4 py-3 safe-area-pb"
          data-testid="mobile-sticky-cta"
        >
          <form
            onSubmit={handleSubscribe}
            className="flex gap-2 max-w-md mx-auto"
          >
            <Input
              type="email"
              required
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="flex-1 bg-white/5 border-white/15 text-white placeholder:text-neutral-500 text-sm h-11 rounded-lg"
              data-testid="mobile-sticky-email"
            />
            <Button
              type="submit"
              disabled={isSubmitting}
              className="bg-[#00F0FF] hover:bg-[#00C2CC] text-black font-bold text-sm h-11 px-4 rounded-lg whitespace-nowrap"
              data-testid="mobile-sticky-submit"
            >
              {isSubmitting ? '...' : 'Get Access'}
            </Button>
          </form>
        </div>
      )}
    </div>
  );
}

export default ComingSoon;

/**
 * Sticky announcement bar above the nav.
 *
 * Style: dark background, cyan text + animated underline on the CTA (per
 * user choice 3B — premium / subtle).
 *
 * Behavior:
 *  - Dismissable; remembers the dismissal via localStorage so returning
 *    visitors never see it twice (per user choice 4A).
 *  - Auto-hides AFTER the launch instant (2026-06-03T20:00:00Z) so it
 *    silently disappears even if no redeploy happens. Backend
 *    /api/site-mode also auto-flips at the same instant, so the entire
 *    ComingSoon page will be replaced by the live site — this is a
 *    secondary belt-and-braces guard.
 */
function LaunchAnnouncementBar() {
  const navigate = useNavigate();
  // 2026-06-03T19:00:00Z == 12:00 PM PDT (noon Pacific time, June 3 in DST)
  const LAUNCH_AT = new Date('2026-06-03T19:00:00Z').getTime();
  const STORAGE_KEY = 'tal_launch_banner_dismissed_v1';

  const [visible, setVisible] = React.useState(() => {
    if (typeof window === 'undefined') return false;
    if (Date.now() >= LAUNCH_AT) return false;
    if (window.localStorage?.getItem(STORAGE_KEY)) return false;
    return true;
  });

  if (!visible) return null;

  const dismiss = (e) => {
    e?.stopPropagation();
    try { window.localStorage?.setItem(STORAGE_KEY, '1'); } catch (_) { /* ignore */ }
    setVisible(false);
  };

  return (
    <div
      role="region"
      aria-label="Launch announcement"
      data-testid="launch-announcement-bar"
      className="bg-black/95 backdrop-blur border-b border-[#00F0FF]/20 text-white text-sm"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-2 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <span
            className="inline-block w-2 h-2 rounded-full bg-[#00F0FF] animate-pulse flex-shrink-0"
            aria-hidden="true"
          />
          <span className="truncate">
            <span className="text-[#00F0FF] font-semibold">Launching June 3, 2026</span>
            <span className="hidden sm:inline text-neutral-400"> — the AI marketplace goes live in</span>
            <span className="sm:hidden text-neutral-400">{' · '}</span>
            <LaunchCountdownInline launchAt={LAUNCH_AT} />
          </span>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <button
            onClick={() => navigate('/submit')}
            data-testid="launch-banner-cta"
            className="text-[#00F0FF] font-semibold text-xs sm:text-sm relative group whitespace-nowrap"
          >
            <span className="hidden sm:inline">Apply for Founding Creator</span>
            <span className="sm:hidden">Apply</span>
            <span aria-hidden className="ml-1">→</span>
            <span
              aria-hidden
              className="absolute left-0 -bottom-0.5 h-0.5 w-0 bg-[#00F0FF] transition-all duration-300 group-hover:w-full"
            />
          </button>
          <button
            onClick={dismiss}
            aria-label="Dismiss launch announcement"
            data-testid="launch-banner-dismiss"
            className="text-neutral-500 hover:text-white px-1.5 ml-1"
          >
            ×
          </button>
        </div>
      </div>
    </div>
  );
}

function LaunchCountdownInline({ launchAt }) {
  const [diff, setDiff] = React.useState(() => Math.max(0, launchAt - Date.now()));
  React.useEffect(() => {
    const t = setInterval(() => setDiff(Math.max(0, launchAt - Date.now())), 60_000);
    return () => clearInterval(t);
  }, [launchAt]);
  const days = Math.floor(diff / 86_400_000);
  const hours = Math.floor((diff % 86_400_000) / 3_600_000);
  if (diff <= 0) return null;
  if (days > 0) return <span className="text-white font-semibold"> {days}d {hours}h</span>;
  return <span className="text-white font-semibold"> {hours}h</span>;
}